import React, { useState, useMemo } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { Select, MenuItem, Box } from "@mui/material";
import { formatTimestampForZone2 } from "../utils/formattimestamp";

const CallActivityGraph = ({ call_data }) => {
  const [dateRange, setDateRange] = useState(30); // Default: Last 30 days
  const [frequency, setFrequency] = useState("daily"); // Default: Daily

  // Transform the call_data to group by selected frequency
const processedData = useMemo(() => {
  const now = new Date();
  const startDate = new Date();

  if (dateRange === 1) {
    // Last 24 hours
    startDate.setDate(now.getDate() - 1);
  } else {
    startDate.setDate(now.getDate() - dateRange);
  }

  const groupedData = {};

  // Step 1: Group call data by daily or hourly keys
  call_data.forEach((call) => {
    let date;
    if (call.callTime.length === 10) {
      date = formatTimestampForZone2(call.callTime * 1000);
    } else if (call.callTime.length === 13) {
      date = formatTimestampForZone2(parseInt(Number(call.callTime)));
    }

    let callDate = date;

    if (callDate < startDate) return; // Filter calls outside the date range

    let key;
    if (frequency === "hourly") {
      key = callDate.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
      });
    } else {
      key = callDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }

    if (!groupedData[key]) {
      groupedData[key] = 0;
    }
    groupedData[key] += 1; // Increment count for each call
  });

  const allDates = [];
  let currentDate = new Date(startDate);

  // Step 2: Generate data based on the frequency
  if (frequency === "weekly") {
    // Weekly aggregation
    while (currentDate <= now) {
      const weekStart = new Date(currentDate);
      weekStart.setDate(currentDate.getDate() - currentDate.getDay()); // Start of the week (Sunday)
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6); // End of the week (Saturday)

      const weekKey = `${weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${weekEnd.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
      
      // Aggregate weekly count
      let weeklyCount = 0;
      for (let d = new Date(weekStart); d <= weekEnd; d.setDate(d.getDate() + 1)) {
        const dailyKey = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        weeklyCount += groupedData[dailyKey] || 0;
      }

      allDates.push({ date: weekKey, count: weeklyCount });
      currentDate = new Date(weekEnd); // Move to the end of the week
      currentDate.setDate(currentDate.getDate() + 1); // Increment to the next day
    }
  } else if (frequency === "hourly") {
    // Hourly aggregation
    while (currentDate <= now) {
      const dayStart = new Date(currentDate);
      for (let hour = 0; hour < 24; hour++) {
        const hourlyKey = new Date(dayStart.setHours(hour, 0, 0, 0)).toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          hour: "2-digit",
        });

        allDates.push({
          date: hourlyKey,
          count: groupedData[hourlyKey] || 0, // Use grouped data if available, else 0
        });
      }
      currentDate.setDate(currentDate.getDate() + 1); // Increment day
    }
  } else {
    // Daily aggregation
    while (currentDate <= now) {
      const dailyKey = currentDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });

      allDates.push({
        date: dailyKey,
        count: groupedData[dailyKey] || 0, // Use grouped data if available, else 0
      });

      currentDate.setDate(currentDate.getDate() + 1); // Increment day
    }
  }

  return allDates;
}, [call_data, dateRange, frequency]);


  return (
    <div
      style={{
        width: "100%",
        height: 300,
        marginBottom: 80,
        padding: 0,
      }}
    >
      {/* Filters */}
      <Box display="flex" justifyContent="flex-end" alignItems={'center'} gap={2} marginBottom={2}>
        <div className="flex flex-1 text-[15px] text-gray-700 font-semibold ">
            Call Activity
        </div>
        <Select
          value={dateRange}
          onChange={(e) => setDateRange(Number(e.target.value))}
          displayEmpty
          sx={{
            fontSize: "14px",
            padding: "4px 8px", // Adjust padding here
            "& .MuiOutlinedInput-notchedOutline": {
              border: "0.6px solid gray",
            },
            "& .MuiSelect-select": {
              padding: "4px 8px", // Adjust padding for the text inside Select
            },
          }}
        >
          <MenuItem value={1}>Last 24 Hours</MenuItem>
          <MenuItem value={7}>Last 7 Days</MenuItem>
          <MenuItem value={30}>Last 30 Days</MenuItem>
          <MenuItem value={60}>Last 60 Days</MenuItem>
          <MenuItem value={90}>Last 90 Days</MenuItem>
        </Select>
        <Select
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
          displayEmpty
          sx={{
            fontSize: "14px",
            padding: "4px 8px", // Adjust padding here
            "& .MuiOutlinedInput-notchedOutline": {
              border: "0.6px solid gray",
            },
            "& .MuiSelect-select": {
              padding: "4px 8px", // Adjust padding for the text inside Select
            },
          }}
        >
          <MenuItem value="hourly">Hourly</MenuItem>
          <MenuItem value="daily">Daily</MenuItem>
          <MenuItem value="weekly">Weekly</MenuItem>
        </Select>
      </Box>

      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={processedData}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }} // Remove default margins
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="date" 
            tickMargin={0} // Reduce margin for ticks
          />
          <YAxis 
            allowDecimals={false} 
            label={{ angle: -90, position: "insideLeft" }} 
            tickMargin={0} // Reduce margin for ticks
          />
          <Tooltip position={''} content={<CustomTooltip />} />
          <Area type="monotone" dataKey="count" stroke="#8884d8" fill="#8884d8" strokeWidth={3} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CallActivityGraph;

const CustomTooltip = ({ active, payload, label }) => {
  if (active) {
    return (
      <div className="tooltip px-4 py-2" style={{ backgroundColor: 'black', color: 'white', textAlign: 'center', borderRadius: 5 }}>
        <p className="text-gray-200 text-[14px]">{label}</p>
        <p>{payload && payload[0]?.value}</p>
      </div>
    );
  }

  return null;
};
